import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { ArrowLeft, ArrowRight, CheckCircle, Phone, CalendarCheck, SpinnerGap } from "@phosphor-icons/react";

const C = { bg: "#070B16", surface: "#0F1729", card: "#111a2e", green: "#10B981", orange: "#FF7A00", text: "#F8FAFC", sub: "#94A3B8", border: "rgba(255,255,255,0.10)" };
const HEAD = { fontFamily: "'Outfit',sans-serif" };
const TZS = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Toronto", "Europe/London", "Europe/Paris", "Africa/Douala", "Africa/Lagos", "Africa/Nairobi", "Asia/Dubai", "Asia/Kolkata", "UTC"];
const CONTACT = ["phone", "sms", "email", "whatsapp"];
const input = "w-full rounded-xl border px-4 py-3 text-sm outline-none";
const inStyle = { background: C.card, borderColor: C.border, color: C.text };

export default function Book() {
  const [services, setServices] = useState([]);
  const [phone, setPhone] = useState("+12029805224");
  const detectedTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "America/New_York"; } })();
  const [f, setF] = useState({
    full_name: "", business_name: "", industry: "", email: "", phone: "", country: "",
    timezone: TZS.includes(detectedTz) ? detectedTz : "America/New_York", services: [], other_service: "",
    recommend_best: false, problem: "", preferred_date: "", preferred_time: "", contact_method: "phone", consent: false,
    company_website: "",
  });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/booking/services").then(({ data }) => { setServices(data.services || []); if (data.phone) setPhone(data.phone); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!f.preferred_date) { setSlots([]); return; }
    setLoadingSlots(true);
    api.get(`/booking/slots?date=${f.preferred_date}&tz=${encodeURIComponent(f.timezone)}`)
      .then(({ data }) => setSlots(data.slots || [])).catch(() => setSlots([])).finally(() => setLoadingSlots(false));
  }, [f.preferred_date, f.timezone]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleService = (s) => setF((st) => ({ ...st, services: st.services.includes(s) ? st.services.filter((x) => x !== s) : [...st.services, s] }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.consent) { setErr("Please tick the consent box so we can contact you."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/booking/submit", f);
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) { setErr(apiError(e2)); } finally { setBusy(false); }
  };

  const phoneHref = `tel:${phone.replace(/[^+\d]/g, "")}`;
  const prettyPhone = "+1 (202) 980-5224";

  if (done) {
    return (
      <div style={{ background: C.bg, color: C.text, fontFamily: "'Manrope',sans-serif" }} className="grid min-h-screen place-items-center px-5">
        <div className="w-full max-w-lg rounded-3xl border p-8 text-center" style={{ background: C.surface, borderColor: C.border }} data-testid="book-success">
          <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-full" style={{ background: `${C.green}22` }}>
            <CheckCircle size={36} weight="fill" style={{ color: C.green }} />
          </div>
          <h1 style={HEAD} className="text-2xl font-black">{done.booked ? "Appointment confirmed 🎉" : "Request received 🎉"}</h1>
          {done.booked && done.appointment ? (
            <p className="mt-3 text-sm" style={{ color: C.sub }}>
              You're booked for <b className="text-white">{done.appointment.date} at {done.appointment.time}</b> ({done.appointment.timezone}).
              A confirmation was sent to your email{f.phone ? " and phone" : ""}.
            </p>
          ) : (
            <p className="mt-3 text-sm" style={{ color: C.sub }}>Thanks! Our team will reach out shortly via {f.contact_method}. A confirmation was sent to your email.</p>
          )}
          <p className="mt-4 text-xs" style={{ color: C.sub }}>To reschedule or cancel, reply to your confirmation email or call {prettyPhone}.</p>
          <Link to="/" data-testid="book-home" className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 font-bold text-white" style={{ background: C.orange }}>Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, color: C.text, fontFamily: "'Manrope',sans-serif" }} className="min-h-screen">
      <header className="sticky top-0 z-40 border-b bg-[#0B1220]/85 backdrop-blur-xl" style={{ borderColor: C.border }}>
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <Link to="/" data-testid="book-back" className="flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white"><ArrowLeft size={18} /> Home</Link>
          <a href={phoneHref} data-testid="book-call" className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-white" style={{ background: C.green }}><Phone size={15} weight="fill" /> {prettyPhone}</a>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-10" data-testid="book-page">
        <span className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wide" style={{ background: `${C.orange}1f`, color: C.orange }}><CalendarCheck size={14} weight="bold" /> Book a Service Appointment</span>
        <h1 style={HEAD} className="mt-4 text-3xl font-black sm:text-4xl">Tell us about your business</h1>
        <p className="mt-2 text-sm" style={{ color: C.sub }}>We'll recommend the right iYeneh solution and book a consultation. Prefer to talk now? Call <a href={phoneHref} className="font-semibold" style={{ color: C.green }}>{prettyPhone}</a> — SI Appointment Receptionist at <b>Extension 10001</b>.</p>

        {err && <div data-testid="book-error" className="mt-5 rounded-xl border px-4 py-3 text-sm" style={{ borderColor: "#ef4444", color: "#fca5a5", background: "#ef444410" }}>{err}</div>}

        <form onSubmit={submit} className="mt-6 space-y-5">
          <input type="text" tabIndex={-1} autoComplete="off" value={f.company_website} onChange={(e) => set("company_website", e.target.value)} className="hidden" aria-hidden />
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className="text-xs font-semibold" style={{ color: C.sub }}>Full name *</label><input data-testid="f-name" required className={input} style={inStyle} value={f.full_name} onChange={(e) => set("full_name", e.target.value)} /></div>
            <div><label className="text-xs font-semibold" style={{ color: C.sub }}>Business name</label><input data-testid="f-business" className={input} style={inStyle} value={f.business_name} onChange={(e) => set("business_name", e.target.value)} /></div>
            <div><label className="text-xs font-semibold" style={{ color: C.sub }}>Industry</label><input data-testid="f-industry" className={input} style={inStyle} value={f.industry} onChange={(e) => set("industry", e.target.value)} placeholder="Dental, HVAC, Salon…" /></div>
            <div><label className="text-xs font-semibold" style={{ color: C.sub }}>Email *</label><input data-testid="f-email" required type="email" className={input} style={inStyle} value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><label className="text-xs font-semibold" style={{ color: C.sub }}>Phone *</label><input data-testid="f-phone" required className={input} style={inStyle} value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 202 555 0100" /></div>
            <div><label className="text-xs font-semibold" style={{ color: C.sub }}>Country</label><input data-testid="f-country" className={input} style={inStyle} value={f.country} onChange={(e) => set("country", e.target.value)} /></div>
            <div><label className="text-xs font-semibold" style={{ color: C.sub }}>Time zone</label>
              <select data-testid="f-tz" className={input} style={inStyle} value={f.timezone} onChange={(e) => set("timezone", e.target.value)}>{TZS.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
            <div><label className="text-xs font-semibold" style={{ color: C.sub }}>Preferred contact</label>
              <select data-testid="f-contact" className={input} style={inStyle} value={f.contact_method} onChange={(e) => set("contact_method", e.target.value)}>{CONTACT.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}</select></div>
          </div>

          <div>
            <label className="text-xs font-semibold" style={{ color: C.sub }}>Services needed (choose all that apply)</label>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {services.map((s) => {
                const on = f.services.includes(s);
                return (
                  <button type="button" key={s} data-testid={`svc-${s}`} onClick={() => toggleService(s)}
                    className="flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors"
                    style={{ borderColor: on ? C.green : C.border, background: on ? `${C.green}18` : C.card, color: C.text }}>
                    <CheckCircle size={16} weight={on ? "fill" : "regular"} style={{ color: on ? C.green : C.sub }} /> {s}
                  </button>
                );
              })}
            </div>
            <div className="mt-2"><input data-testid="f-other" className={input} style={inStyle} value={f.other_service} onChange={(e) => set("other_service", e.target.value)} placeholder="Other service (airtime/data top-up, eSIM, hosting, meetings, SMS, fax…)" /></div>
            <label className="mt-2 flex items-center gap-2 text-sm" style={{ color: C.sub }}>
              <input type="checkbox" data-testid="f-recommend" checked={f.recommend_best} onChange={(e) => set("recommend_best", e.target.checked)} className="h-4 w-4" style={{ accentColor: C.orange }} /> Recommend the best solution for me
            </label>
          </div>

          <div><label className="text-xs font-semibold" style={{ color: C.sub }}>Your current business or missed-call problem</label>
            <textarea data-testid="f-problem" rows={3} className={input} style={inStyle} value={f.problem} onChange={(e) => set("problem", e.target.value)} placeholder="e.g. We miss calls during jobs and lose new customers." /></div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className="text-xs font-semibold" style={{ color: C.sub }}>Preferred date</label>
              <input data-testid="f-date" type="date" min={new Date().toISOString().slice(0, 10)} className={input} style={inStyle} value={f.preferred_date} onChange={(e) => { set("preferred_date", e.target.value); set("preferred_time", ""); }} /></div>
            <div><label className="text-xs font-semibold" style={{ color: C.sub }}>Preferred time {loadingSlots && <SpinnerGap size={12} className="inline animate-spin" />}</label>
              {f.preferred_date ? (
                slots.length ? (
                  <div className="mt-1 flex max-h-28 flex-wrap gap-1.5 overflow-auto">
                    {slots.map((s) => (
                      <button type="button" key={s} data-testid={`slot-${s}`} onClick={() => set("preferred_time", s)}
                        className="rounded-lg border px-2.5 py-1 text-xs font-semibold"
                        style={{ borderColor: f.preferred_time === s ? C.orange : C.border, background: f.preferred_time === s ? `${C.orange}22` : C.card, color: C.text }}>{s}</button>
                    ))}
                  </div>
                ) : <p className="mt-2 text-xs" style={{ color: C.sub }}>{loadingSlots ? "Checking availability…" : "No open times that day — pick another date or leave blank and we'll call you."}</p>
              ) : <p className="mt-2 text-xs" style={{ color: C.sub }}>Pick a date to see open times (optional).</p>}
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm" style={{ color: C.sub }}>
            <input type="checkbox" data-testid="f-consent" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5 h-4 w-4" style={{ accentColor: C.green }} />
            <span>I consent to iYeneh contacting me by phone, SMS and email about my request. *</span>
          </label>

          <button type="submit" data-testid="book-submit" disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-full px-7 py-4 font-bold text-white shadow-xl transition-transform hover:-translate-y-0.5 disabled:opacity-60" style={{ background: C.orange }}>
            {busy ? "Submitting…" : f.preferred_time ? "Book my appointment" : "Send my request"} <ArrowRight size={18} weight="bold" />
          </button>
        </form>
      </div>
    </div>
  );
}
